import type { Battle, Side, Tick } from "./game.js";

export const COIN: Record<string, { bg: string; fg: string; label: string }> = {
  BTC: { bg: "#F7931A", fg: "#0B0E14", label: "B" },
  ETH: { bg: "#627EEA", fg: "#FFFFFF", label: "E" },
  SOL: { bg: "linear-gradient(135deg, #9945FF, #14F195)", fg: "#0B0E14", label: "S" },
  TON: { bg: "#0098EA", fg: "#FFFFFF", label: "T" },
  XAU: { bg: "linear-gradient(135deg, #FFE08A, #C9932B)", fg: "#0B0E14", label: "Au" },
};

export function pairLabel(arena: string): string {
  return arena === "XAU" ? "GOLD/USD" : `${arena}/USD`;
}

/** Prices since the battle's start price was locked, plus where things stand for this player. */
export function battleState(battle: Battle, ticks: Tick[] | undefined, mySide: Side) {
  const start = Number(battle.startPrice);
  const since = (ticks ?? []).filter((t) => battle.startedAt !== null && t.t >= battle.startedAt);
  const last = since.length ? since[since.length - 1]!.p : start;
  const move = last - start;
  const winning = move === 0 ? null : (move > 0) === (mySide === "long");
  // The lead meter leans 5 points per basis point of move, kept between 5% and 95%.
  const bps = (move / start) * 10_000;
  const longShare = Math.max(5, Math.min(95, Math.round(50 + bps * 5)));
  const myShare = mySide === "long" ? longShare : 100 - longShare;
  return { start, since, last, move, winning, myShare };
}

/** Chart geometry for the arena card: line, filled area, entry line and the live dot. */
export function chart(battle: Battle, since: Tick[], start: number, w = 300, h = 170, top = 20, bottom = 140) {
  const durationMs = (battle.endsAt ?? 0) - (battle.startedAt ?? 0) || 180_000;
  const ps = [start, ...since.map((t) => t.p)];
  const lo = Math.min(...ps);
  const hi = Math.max(...ps);
  const span = hi - lo || start * 0.0002 || 1;
  const pad = span * 0.15;
  const y = (p: number) => top + (1 - (p - (lo - pad)) / (span + pad * 2)) * (bottom - top);
  const x = (t: number) => Math.min(w, ((t - (battle.startedAt ?? t)) / durationMs) * w);
  const pts = [{ x: 0, y: y(start) }, ...since.map((t) => ({ x: x(t.t), y: y(t.p) }))];
  const line = pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const lastPt = pts[pts.length - 1]!;
  const area = `M${pts.map((p) => `${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" L")} L${lastPt.x.toFixed(1)} ${h} L0 ${h} Z`;
  return { line, area, entryY: y(start), dot: lastPt };
}
